import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import i18n from 'i18n-js';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Typography from '@material-ui/core/Typography';
import { ListItemIcon, ListItemText } from '@material-ui/core';

import { default as menusStore } from 'store/menusStore';
import { ApplicationState } from 'store';
import { BasicThreeDotsMenu, PopupConfirmation, Text } from 'components/shared';
import { MenuType, Positions, Roles } from 'constants/enums';
import { MenuEditModal } from '../../Modals/MenuEditModal';
import { getHourAndMinutes } from 'helpers/datetimeHelper';
import { useStyles } from '../styles';
import { images } from 'assets';
import MenuCopyModal from 'components/menu/Modals/MenuCopyModal';
import { MenuDetailsData } from 'store/menuDetailsStore/reducer';
import { MenuData } from 'store/menusStore/reducer';
import clsx from 'clsx';
import { RemoveCircleOutline, SettingsBackupRestore } from '@material-ui/icons';
import { theme } from 'config/theme';

enum ConfirmationType {
    Hidden,
    Disable,
    Delete,
    CatalogManagerDisable,
    CatalogManagerDisableForRestaurant,
    CatalogParticipantDisable,
}

export interface MenuPreviewProps {
    menu: MenuData;
    disable: any;
    edit: any;
    menuType: string;
    showDetails?: (id: string) => void;
    setName?: (name: string) => void;
    toggleRestaurantCatalogMenuDisable?: (menu: MenuData) => void;
}

export default function MenuPreviewItem(props: MenuPreviewProps) {
    const history = useHistory();
    const classes = useStyles();
    const dispatch = useDispatch();
    const user = useSelector((state: ApplicationState) => state.user);
    const currentPosition = user.restaurants.find(
        (i) => i.id === user.currentRestaurantId
    )?.currentPosition;
    const restaurantName = user.restaurants.find((i) => i.id === user.currentRestaurantId)?.name;
    const { menu } = props;
    const [showEdit, setEdit] = React.useState<boolean>(false);
    const [showConfirmation, setConfirmation] = useState<ConfirmationType>(ConfirmationType.Hidden);
    const [showCopyDialog, setShowCopyDialog] = useState(false);
    const isIngredients = props.menuType === 'ING';
    const isManager = user.role === Roles.OWNER && currentPosition === Positions.MANAGER;
    const isCatalogManager = menu.type === MenuType.Catalog
        && menu.catalogData?.allowToEditCatalog
        && isManager;

    function handleClick(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        isIngredients ? history.push('/ingredients') : handleDetails(e);
    }

    function handleClickOpen() {
        setEdit(true);
    }

    function handleClose() {
        setEdit(false);
    }

    function handleConfirmationClose() {
        setConfirmation(ConfirmationType.Hidden);
    }

    function disableMenu() {
        props.disable(props.menu.id);
        handleConfirmationClose();
    }

    function deleteMenu() {
        dispatch(menusStore.actionCreators.deleteMenu(props.menu.id));
        handleConfirmationClose();
    }

    function prepareBackground() {
        let styles;
        if (props.menu.photo && isIngredients) {
            styles = {
                background: `url(${props.menu.photo})`,
            };
        } else if (props.menu.photo) {
            styles = {
                background: `linear-gradient(rgba(0,0,0,.5), rgba(0,0,0,.5)), url(${props.menu.photo})`,
            };
        }

        return styles;
    }

    function handleDetails(e: React.MouseEvent<HTMLDivElement, MouseEvent>) {
        e.stopPropagation();
        e.preventDefault();
        if (props.showDetails) {
            if (props.menu.isEnabled || user.role === Roles.OWNER) {
                !!props.setName && props.setName(props.menu.name);
                props.showDetails(props.menu.id);
            }
        } else {
            history.push(`/menu/${props.menu.id}`);
        }
    }

    async function disableForRestaurant() {
        props.toggleRestaurantCatalogMenuDisable?.(menu);
    }

    function getThreeDotItems() {
        const editItem = (
            <div className={classes.threeDotsMenuItemContainer} onClick={handleClickOpen}>
                <ListItemIcon>
                    <img src={images.icons.edit} alt="edit" height={18} />
                </ListItemIcon>
                <Typography variant="inherit">{i18n.t('common.edit')}</Typography>
            </div>
        );
        const disableItem = (
            <div className={classes.threeDotsMenuItemContainer} onClick={() => setConfirmation(ConfirmationType.Disable)}>
                <ListItemIcon style={{ color: theme.palette.text.disabled }}>
                    {!menu.isEnabled
                        ? <SettingsBackupRestore style={{ width: 18 }} />
                        : <RemoveCircleOutline style={{ width: 18 }} />
                    }
                </ListItemIcon>
                <Typography variant="inherit">
                    {menu.isEnabled ? i18n.t('common.disable') : i18n.t('common.enable')}
                </Typography>
            </div>
        );
        const copyItem = (
            <div className={classes.threeDotsMenuItemContainer} onClick={() => setShowCopyDialog(true)}>
                <ListItemIcon>
                    <img src={images.icons.copy} alt="copy" height={18} />
                </ListItemIcon>
                <Typography variant="inherit">
                    {i18n.t('restaurant.copyAndMakeSpecialMenu')}
                </Typography>
            </div>
        );
        const deleteItem = (
            <div className={classes.threeDotsMenuItemContainer} onClick={() => setConfirmation(ConfirmationType.Delete)}>
                <ListItemIcon>
                    <img src={images.icons.delete} alt="edit" height={18} />
                </ListItemIcon>
                <Typography variant="inherit">{i18n.t('common.delete')}</Typography>
            </div>
        );
        const managerEditItem = (
            <div className={classes.threeDotsMenuItemContainer} onClick={() => setConfirmation(ConfirmationType.CatalogManagerDisable)}>
                <ListItemIcon style={{ color: theme.palette.text.disabled }}>
                    {menu.catalogData?.isDisabled
                        ? <SettingsBackupRestore style={{ width: 18 }} />
                        : <RemoveCircleOutline style={{ width: 18 }} />
                    }
                </ListItemIcon>
                <ListItemText
                    primary={!menu.catalogData?.isDisabled
                        ? i18n.t('restaurant.disableForRestaurant', { name: i18n.t('common.group.one') })
                        : i18n.t('restaurant.enableForRestaurant', { name: i18n.t('common.group.one') })
                    }
                />
            </div>
        );
        const managerEditForRestaurantItem = (
            <div className={classes.threeDotsMenuItemContainer} onClick={() => setConfirmation(ConfirmationType.CatalogManagerDisableForRestaurant)}>
                <ListItemIcon style={{ color: theme.palette.text.disabled }}>
                    {!menu.isEnabled
                        ? <SettingsBackupRestore style={{ width: 18 }} />
                        : <RemoveCircleOutline style={{ width: 18 }} />
                    }
                </ListItemIcon>
                <ListItemText
                    primary={menu.isEnabled
                        ? i18n.t('restaurant.disableForRestaurant', { name: restaurantName })
                        : i18n.t('restaurant.enableForRestaurant', { name: restaurantName })
                    }
                />
            </div>
        );
        const participantEditForRestaurantItem = (
            <div className={classes.threeDotsMenuItemContainer} onClick={() => setConfirmation(ConfirmationType.CatalogParticipantDisable)}>
                <ListItemIcon>
                    <img src={images.icons.removeCircleDisabled} alt="edit" height={18} />
                </ListItemIcon>
                <ListItemText
                    primary={menu.isEnabled ? i18n.t('common.disable') : i18n.t('common.enable')}
                />
            </div>
        );

        switch (true) {
            case menu.type === MenuType.Standard:
            case menu.type === MenuType.Special:
                return [editItem, disableItem, copyItem, deleteItem];
            case menu.type === MenuType.Catalog && !isCatalogManager:
                return [participantEditForRestaurantItem];
            case menu.type === MenuType.Catalog && isCatalogManager:
                const items = [editItem, managerEditItem];
                if (!menu.catalogData?.isDisabled) {
                    items.push(managerEditForRestaurantItem);
                }
                return items;
            default:
                return [editItem, disableItem, copyItem];
        }
    }

    return (
        <div>
            <Card
                onClick={(e) => {
                    handleClick(e);
                }}
                className={clsx(classes.menuPreviewItemRoot, {
                    [classes.imageBased]: menu.photo,
                    [classes.disabled]:
                        menu.type === MenuType.Catalog && isCatalogManager
                            ? menu.catalogData?.isDisabled
                            : !menu.isEnabled,
                })}
                style={prepareBackground()}
            >
                {!menu.isEnabled && isCatalogManager && (
                    <div className={classes.cardHeaderLabel}>
                        <Text text={i18n.t('catalog.disabledForRestaurantText', { name: restaurantName })} maxLength={40} fontSize={12} />
                    </div>
                )}
                <CardHeader
                    action={
                        isManager &&
                        !isIngredients && <BasicThreeDotsMenu items={getThreeDotItems()} />
                    }
                    title={<Text text={props.menu.name} maxLength={20} fontSize={21} />}
                    className={!!props.menu.photo ? classes.whiteThreeDots : ''}
                    //Description hidded for now but maybe will be showed in feature
                    //subheader={props.menu.description}
                />
                <CardActions disableSpacing>
                    <CardContent>
                        {!isIngredients && (
                            <Typography variant="body2" color="textSecondary" component="p">
                                {props.menu.availableFrom &&
                                props.menu.availableTo &&
                                props.menu.availableFrom !== props.menu.availableTo
                                    ? `${getHourAndMinutes(
                                          props.menu.availableFrom
                                      )} - ${getHourAndMinutes(props.menu.availableTo)}`
                                    : '24/7'}
                            </Typography>
                        )}
                    </CardContent>
                    <CardContent>
                        <Typography variant="subtitle2" color="textSecondary" component="p">
                            {`${props.menu.dishesCount} items`}
                        </Typography>
                    </CardContent>
                </CardActions>
            </Card>
            <MenuEditModal
                menu={props.menu}
                isOpen={showEdit}
                handleClose={handleClose}
                isDetails={false}
            />
            <PopupConfirmation
                open={showConfirmation !== ConfirmationType.Hidden}
                close={handleConfirmationClose}
                action={() => {
                    switch (showConfirmation) {
                        case ConfirmationType.Disable:
                        case ConfirmationType.CatalogManagerDisable:
                            disableMenu();
                            break;
                        case ConfirmationType.Delete:
                                deleteMenu();
                                break;
                        case ConfirmationType.CatalogManagerDisableForRestaurant:
                        case ConfirmationType.CatalogParticipantDisable:
                            disableForRestaurant();
                            break;
                        default:
                            break;
                    }
                }}
                title={(() => {
                    switch (showConfirmation) {
                        case ConfirmationType.CatalogManagerDisable:
                            return menu.catalogData?.isDisabled ? i18n.t('common.enable') : i18n.t('common.disable');
                        case ConfirmationType.Delete:
                            return i18n.t('common.delete');
                        default:
                            return menu.isEnabled ? i18n.t('common.disable') : i18n.t('common.enable');
                    }
                })()}
                description={(() => {
                    switch (showConfirmation) {
                        case ConfirmationType.CatalogManagerDisable:
                            return menu.catalogData?.isDisabled ? i18n.t('confirmation.enableMenu') : i18n.t('confirmation.disableMenu');
                        case ConfirmationType.Delete:
                            return i18n.t('confirmation.deleteMenu');
                        default:
                            return menu.isEnabled ? i18n.t('confirmation.disableMenu') : i18n.t('confirmation.enableMenu');
                    }
                })()}
                activeBtn={(() => {
                    switch (showConfirmation) {
                        case ConfirmationType.CatalogManagerDisable:
                            return menu.catalogData?.isDisabled ? i18n.t('common.enable') : i18n.t('common.disable');
                        case ConfirmationType.Delete:
                            return i18n.t('common.delete');
                        default:
                            return menu.isEnabled ? i18n.t('common.disable') : i18n.t('common.enable');
                    }
                })()}
            />
            <MenuCopyModal
                menu={props.menu as unknown as MenuDetailsData}
                isOpen={showCopyDialog}
                onClose={() => setShowCopyDialog(false)}
            />
        </div>
    );
}
